import { Tenant } from '../types';

const RECENT_TENANTS_KEY = 'recentTenants';
const MAX_RECENT_TENANTS = 5;

/**
 * Get list of recently accessed tenants
 */
export function getRecentTenants(): number[] {
  try {
    const stored = localStorage.getItem(RECENT_TENANTS_KEY);
    if (!stored) return [];
    const parsed = JSON.parse(stored);
    return Array.isArray(parsed) ? parsed : [];
  } catch {
    return [];
  }
}

/**
 * Add a tenant to the recent list
 */
export function addRecentTenant(tenantId: number): void {
  try {
    const recent = getRecentTenants();

    // Remove if already exists
    const filtered = recent.filter(id => id !== tenantId);

    // Add to front of list
    const updated = [tenantId, ...filtered].slice(0, MAX_RECENT_TENANTS);

    localStorage.setItem(RECENT_TENANTS_KEY, JSON.stringify(updated));
  } catch (error) {
    console.error('Failed to save recent tenant:', error);
  }
}

/**
 * Clear recent tenants list
 */
export function clearRecentTenants(): void {
  localStorage.removeItem(RECENT_TENANTS_KEY);
}

/**
 * Sort tenants by recent usage
 * Recent tenants appear first, followed by alphabetically sorted remaining tenants
 */
export function sortTenantsByRecent(tenants: Tenant[]): Tenant[] {
  const recentIds = getRecentTenants();
  const recentTenants: Tenant[] = [];
  const otherTenants: Tenant[] = [];

  tenants.forEach(tenant => {
    if (recentIds.includes(tenant.tenantId)) {
      recentTenants.push(tenant);
    } else {
      otherTenants.push(tenant);
    }
  });

  // Sort recent tenants by their position in recent list
  recentTenants.sort((a, b) => {
    const aIndex = recentIds.indexOf(a.tenantId);
    const bIndex = recentIds.indexOf(b.tenantId);
    return aIndex - bIndex;
  });

  // Sort other tenants alphabetically
  otherTenants.sort((a, b) => a.tenantName.localeCompare(b.tenantName));

  return [...recentTenants, ...otherTenants];
}

/**
 * Get tenant display name with code
 */
export function getTenantDisplayName(tenant: Tenant, format: 'name-code' | 'code-name' = 'name-code'): string {
  if (format === 'code-name') {
    return `${tenant.tenantCode} - ${tenant.tenantName}`;
  }
  return `${tenant.tenantName} (${tenant.tenantCode})`;
}

/**
 * Check if user has access to multiple tenants
 */
export function hasMultipleTenants(tenants: Tenant[]): boolean {
  return tenants.length > 1;
}

/**
 * Get tenant by ID from list
 */
export function getTenantById(tenants: Tenant[], tenantId: number): Tenant | undefined {
  return tenants.find(t => t.tenantId === tenantId);
}

/**
 * Get tenant by code from list
 */
export function getTenantByCode(tenants: Tenant[], code: string): Tenant | undefined {
  return tenants.find(t => t.tenantCode.toLowerCase() === code.toLowerCase());
}

/**
 * Detect subdomain from current URL
 * Returns null if no tenant subdomain is detected (admin portal or localhost)
 */
export interface SubdomainDetectionResult {
  subdomain: string | null;
  isTenantLogin: boolean;
  isAdminPortal: boolean;
}

export function detectSubdomain(): SubdomainDetectionResult {
  const host = window.location.hostname;
  const parts = host.split('.');

  // Check if subdomain exists (e.g., acme.mdm.com)
  // Exclude common patterns: localhost, www, admin, api, 127
  if (parts.length >= 2) {
    const firstPart = parts[0].toLowerCase();

    // Special cases - admin portal or API
    if (firstPart === 'admin' || firstPart === 'api') {
      return {
        subdomain: null,
        isTenantLogin: false,
        isAdminPortal: firstPart === 'admin'
      };
    }

    // Exclude localhost and www
    if (firstPart !== 'localhost' &&
        firstPart !== 'www' &&
        firstPart !== '127') {
      return {
        subdomain: firstPart,
        isTenantLogin: true,
        isAdminPortal: false
      };
    }
  }

  // Default - admin portal (no subdomain)
  return {
    subdomain: null,
    isTenantLogin: false,
    isAdminPortal: true
  };
}

/**
 * Get subdomain from window location
 * @deprecated Use detectSubdomain() instead for more detailed information
 */
export function getSubdomain(): string | null {
  return detectSubdomain().subdomain;
}
